import { apiService } from './api';

// Dashboard API Types
export interface DashboardOverview {
  todayRevenue: number;
  todayOrders: number;
  activeOrders: number;
  totalTables: number;
  availableTables: number;
  totalItems: number;
  availableItems: number;
  outOfStockItems: number;
}

export interface TodaySummary {
  totalOrders: number;
  grossAmount: number;
  discountGiven: number;
  netRevenue: number;
  avgOrderValue: number;
}

export interface TableStatusReport {
  available: number;
  occupied: number;
  reserved: number;
  outOfService: number;
}

export interface SalesTrendData {
  daily: Array<{
    date: string;
    revenue: number;
    orders: number;
    avgOrderValue: number;
  }>;
  comparison: {
    revenueGrowth: number;
    ordersGrowth: number;
    positive: boolean;
  };
}

export interface TopItem {
  itemId: string;
  itemName: string;
  category: string;
  quantitySold: number;
  revenue: number;
  percentage: number;
}

export interface PaymentMethodReport {
  method: string;
  amount: number;
  count: number;
  percentage: number;
}

export interface CategoryWiseSale {
  category: string;
  itemsSold: number;
  revenue: number;
  percentage: number;
}

export interface RecentOrder {
  orderId: string;
  orderNumber: string;
  customerName: string;
  tableNumber: string;
  items: number;
  amount: number;
  status: string;
  createdAt: string;
  timeElapsed: string;
}

export interface OutstandingDue {
  orderId: string;
  customerName: string;
  customerMobile: string;
  amount: number;
  orderDate: string;
  daysOverdue: number;
}

export interface OutstandingDues {
  totalAmount: number;
  count: number;
  dues: OutstandingDue[];
}

export interface HourlyPerformance {
  hour: string;
  orders: number;
  revenue: number;
  peakHour: boolean;
}

export interface StaffPerformance {
  userId: string;
  userName: string;
  ordersHandled: number;
  totalRevenue: number;
  avgOrderValue: number;
}

export interface Alert {
  type: string;
  priority: string;
  message: string;
  timestamp: string;
}

export interface DashboardData {
  overview: DashboardOverview;
  todaySummary: TodaySummary;
  tableStatus: TableStatusReport;
  salesTrend: SalesTrendData;
  topItems: TopItem[];
  paymentMethods: PaymentMethodReport[];
  categoryWiseSales: CategoryWiseSale[];
  recentOrders: RecentOrder[];
  outstandingDues: OutstandingDues;
  hourlyPerformance: HourlyPerformance[];
  staffPerformance: StaffPerformance[];
  alerts: Alert[];
}

// Order Details Report Types
export interface OrderDetailItem {
  itemName: string;
  quantity: number;
  unitPrice: number;
  amount: number;
}

export interface OrderDetail {
  orderId: string;
  orderNumber: string;
  orderDate: string;
  customerName: string;
  customerMobile: string;
  tableName: string;
  totalItems: number;
  subTotal: number;
  discount: number;
  total: number;
  paymentMode: string;
  status: string;
  items: OrderDetailItem[];
}

export interface OrderDetailsReport {
  orders: OrderDetail[];
  summary: {
    totalOrders: number;
    totalRevenue: number;
    totalDiscount: number;
    netRevenue: number;
  };
}

// Dues Details Report Types
export interface DueDetail {
  orderId: string;
  orderNumber: string;
  orderDate: string;
  customerName: string;
  customerMobile: string;
  totalAmount: number;
  paidAmount: number;
  dueAmount: number;
  daysOverdue: number;
}

export interface DuesDetailsReport {
  dues: DueDetail[];
  summary: {
    totalDues: number;
    totalDueAmount: number;
    totalPaidAmount: number;
    totalOutstanding: number;
  };
}

// Item-wise Details Report Types
export interface ItemWiseDetail {
  itemId: string;
  itemName: string;
  category: string;
  quantitySold: number;
  unitPrice: number;
  totalRevenue: number;
  percentage: number;
}

export interface ItemWiseDetailsReport {
  items: ItemWiseDetail[];
  summary: {
    totalItems: number;
    totalQuantity: number;
    totalRevenue: number;
  };
}

// Table-wise Details Report Types
export interface TableWiseDetail {
  tableId: string;
  tableName: string;
  totalOrders: number;
  totalRevenue: number;
  avgOrderValue: number;
  percentage: number;
}

export interface TableWiseDetailsReport {
  tables: TableWiseDetail[];
  summary: {
    totalTables: number;
    totalOrders: number;
    totalRevenue: number;
  };
}

// Daily Closing Report Types
export interface DailyClosingPayment {
  mode: string;
  amount: number;
  count: number;
}

export interface DailyClosingCategorySale {
  category: string;
  revenue: number;
}

export interface DailyClosingReport {
  date: string;
  sales: {
    totalOrders: number;
    grossSales: number;
    discounts: number;
    netSales: number;
  };
  paymentBreakdown: DailyClosingPayment[];
  categorySales: DailyClosingCategorySale[];
  expenses: {
    total: number;
  };
  summary: {
    totalCash: number;
    totalCard: number;
    totalUPI: number;
    totalDues: number;
    netClosing: number;
  };
}

// Customer-wise Details Report Types
export interface CustomerWiseDetail {
  customerId: string;
  customerName: string;
  customerMobile: string;
  totalOrders: number;
  totalSpent: number;
  avgOrderValue: number;
  lastOrderDate: string;
}

export interface CustomerWiseDetailsReport {
  customers: CustomerWiseDetail[];
  summary: {
    totalCustomers: number;
    totalOrders: number;
    totalRevenue: number;
  };
}

// Kitchen-wise Details Report Types
export interface KitchenWiseDetail {
  kitchenId: string;
  kitchenName: string;
  totalOrders: number;
  itemsSold: number;
  totalRevenue: number;
  percentage: number;
}

export interface KitchenWiseDetailsReport {
  kitchens: KitchenWiseDetail[];
  summary: {
    totalKitchens: number;
    totalOrders: number;
    totalRevenue: number;
  };
}

// Report Filters
export interface ReportFilters {
  org: string;
  start?: string;
  end?: string;
  format?: 'json' | 'pdf' | 'excel';
  restaurantId?: string;
}

export const reportService = {
  // 1. Dashboard API
  async getDashboardData(organizationCode: string, restaurantId?: string | null): Promise<DashboardData> {
    const response = await apiService.get<DashboardData>('/reports/dashboard', {
      org: organizationCode,
      ...(restaurantId && { restaurantId })
    });
    return response.data;
  },

  // 2. Order Details Report
  async getOrderDetails(filters: ReportFilters): Promise<OrderDetailsReport | Blob> {
    if (filters.format === 'pdf' || filters.format === 'excel') {
      const response = await apiService.get<Blob>('/reports/order-details', filters, {
        responseType: 'blob'
      });
      return response.data;
    }
    const response = await apiService.get<OrderDetailsReport>('/reports/order-details', filters);
    return response.data;
  },

  // 3. Dues Details Report
  async getDuesDetails(filters: ReportFilters): Promise<DuesDetailsReport | Blob> {
    if (filters.format === 'pdf' || filters.format === 'excel') {
      const response = await apiService.get<Blob>('/reports/dues-details', filters, {
        responseType: 'blob'
      });
      return response.data;
    }
    const response = await apiService.get<DuesDetailsReport>('/reports/dues-details', filters);
    return response.data;
  },

  // 4. Item-wise Details Report
  async getItemWiseDetails(filters: ReportFilters): Promise<ItemWiseDetailsReport | Blob> {
    if (filters.format === 'pdf' || filters.format === 'excel') {
      const response = await apiService.get<Blob>('/reports/item-wise-details', filters, {
        responseType: 'blob'
      });
      return response.data;
    }
    const response = await apiService.get<ItemWiseDetailsReport>('/reports/item-wise-details', filters);
    return response.data;
  },

  // 5. Table-wise Details Report
  async getTableWiseDetails(filters: ReportFilters): Promise<TableWiseDetailsReport | Blob> {
    if (filters.format === 'pdf' || filters.format === 'excel') {
      const response = await apiService.get<Blob>('/reports/table-wise-details', filters, {
        responseType: 'blob'
      });
      return response.data;
    }
    const response = await apiService.get<TableWiseDetailsReport>('/reports/table-wise-details', filters);
    return response.data;
  },

  // 6. Daily Closing Report
  async getDailyClosing(organizationCode: string, format?: 'json' | 'pdf' | 'excel', restaurantId?: string | null): Promise<DailyClosingReport | Blob> {
    const params = {
      org: organizationCode,
      ...(restaurantId && { restaurantId })
    };
    if (format === 'pdf' || format === 'excel') {
      const response = await apiService.get<Blob>('/reports/daily-closing', { ...params, format }, {
        responseType: 'blob'
      });
      return response.data;
    }
    const response = await apiService.get<DailyClosingReport>('/reports/daily-closing', params);
    return response.data;
  },

  // 7. Customer-wise Details Report
  async getCustomerWiseDetails(filters: ReportFilters): Promise<CustomerWiseDetailsReport | Blob> {
    if (filters.format === 'pdf' || filters.format === 'excel') {
      const response = await apiService.get<Blob>('/reports/customer-wise-details', filters, {
        responseType: 'blob'
      });
      return response.data;
    }
    const response = await apiService.get<CustomerWiseDetailsReport>('/reports/customer-wise-details', filters);
    return response.data;
  },

  // 8. Kitchen-wise Details Report
  async getKitchenWiseDetails(filters: ReportFilters): Promise<KitchenWiseDetailsReport | Blob> {
    if (filters.format === 'pdf' || filters.format === 'excel') {
      const response = await apiService.get<Blob>('/reports/kitchen-wise-details', filters, {
        responseType: 'blob'
      });
      return response.data;
    }
    const response = await apiService.get<KitchenWiseDetailsReport>('/reports/kitchen-wise-details', filters);
    return response.data;
  }
};
